import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Heart, Users, MapPin, BookOpen, Leaf, Star, ArrowRight, BadgeCheck } from "lucide-react";
import Seo from "../components/Seo";
import { isPrerendered } from "../lib/hydration";
import { BUSINESS, getOrganizationSchema, getSameAsUrls } from "../lib/businessInfo";

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, delay: i * 0.1, ease: "easeOut" },
  }),
};

const differentiators = [
  {
    icon: BookOpen,
    title: "Authentic Yoga Traditions",
    description:
      "Learn from experienced teachers who honor the traditional roots of yoga while making the teachings accessible for modern life.",
  },
  {
    icon: Leaf,
    title: "Immersive Retreat Experiences",
    description:
      "Our retreats combine yoga, meditation, nature, wellness practices, and cultural experiences to create meaningful and memorable journeys.",
  },
  {
    icon: Users,
    title: "Small & Supportive Community",
    description:
      "We believe transformation happens best in a welcoming environment where every guest feels seen, supported, and valued.",
  },
  {
    icon: MapPin,
    title: "Beautiful Locations",
    description:
      "From the Himalayan foothills of Rishikesh to the coastal serenity of Goa, our destinations are chosen to inspire relaxation and renewal.",
  },
];

const stats = [
  { value: "20+", label: "Destinations" },
  { value: "4.8★", label: "Average Rating" },
];

const jsonLd = getOrganizationSchema({
  url: "https://yogaretreatsbooking.com",
  description:
    "Transformative yoga and meditation retreats in India. Find authentic yoga retreat experiences in Rishikesh, Goa, and across India.",
  knowsAbout: ["Yoga Retreats", "Meditation Retreats", "Yoga Teacher Training", "Ayurveda", "Wellness"],
  sameAs: getSameAsUrls(),
});

function About() {
  return (
    <div className="min-h-screen bg-white">
      <Seo
        title="About Us — Our Story & Mission"
        description="Learn about Yoga Retreats Booking — our mission to make authentic yoga, meditation, and holistic wellness accessible to everyone. Transformative retreats in Rishikesh, Goa, and across India."
        canonicalPath="/about"
        jsonLd={jsonLd}
      />

      {/* Hero */}
      <section className="relative pt-16 overflow-hidden bg-gradient-to-br from-emerald-900 via-teal-800 to-emerald-700">
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=1600&q=80')",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-24 text-center">
          <motion.div
            initial={isPrerendered ? false : "hidden"}
            animate="visible"
            variants={fadeUp}
            custom={0}
          >
            <span className="inline-block px-3 py-1 rounded-full bg-emerald-500/30 text-emerald-100 text-sm font-medium mb-4 border border-emerald-400/40">
              Our Story
            </span>
            <h1 className="text-4xl sm:text-5xl font-bold text-white mb-6 leading-tight">
              A Journey Back{" "}
              <span className="text-emerald-300">to Yourself</span>
            </h1>
            <p className="text-lg text-emerald-100/90 max-w-2xl mx-auto leading-relaxed">
              In a world that moves faster every day, finding moments of stillness has become
              more important than ever.
            </p>
          </motion.div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-16 bg-white" style={{ clipPath: "ellipse(55% 100% at 50% 100%)" }} />
      </section>

      {/* Intro */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <motion.div
          initial={isPrerendered ? false : "hidden"}
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUp}
          className="text-center"
        >
          <p className="text-lg text-gray-600 leading-relaxed mb-6">
            At <strong className="text-gray-900">Yoga Retreats Booking</strong>, we create transformative yoga and
            meditation retreats that help you step away from daily distractions, reconnect with
            your inner self, and rediscover balance in body, mind, and spirit.
          </p>
          <p className="text-lg text-gray-600 leading-relaxed mb-6">
            Our retreats are inspired by the timeless wisdom of{" "}
            <strong className="text-emerald-700">Yoga, Meditation, and Ayurveda</strong> — ancient practices
            that have guided seekers toward health, clarity, and inner peace for thousands of years.
          </p>
          <p className="text-lg text-gray-600 leading-relaxed">
            Whether you join us in the spiritual energy of{" "}
            <strong className="text-gray-900">Rishikesh</strong>, the peaceful beaches of{" "}
            <strong className="text-gray-900">Goa</strong>, or one of our carefully selected retreat
            destinations across India, every experience is designed to support your personal
            journey of healing, growth, and self-discovery.
          </p>
        </motion.div>
      </section>

      {/* Registered business */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-4">
        <motion.div
          initial={isPrerendered ? false : "hidden"}
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUp}
          className="rounded-2xl border border-emerald-100 bg-gradient-to-br from-emerald-50 to-white p-6 sm:p-8"
        >
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center flex-shrink-0">
              <BadgeCheck className="text-emerald-700" size={24} />
            </div>
            <div>
              <p className="text-sm font-semibold uppercase tracking-wider text-emerald-700 mb-1">
                Registered Indian Business
              </p>
              <h2 className="text-xl font-bold text-gray-900 mb-2">{BUSINESS.tradeName}</h2>
              <p className="text-gray-600 text-sm leading-relaxed">
                {BUSINESS.udyamLabel}. We help travellers find trusted yoga retreats and wellness programs
                across India.
              </p>
              <p className="text-sm text-gray-600 mt-4">
                <span className="font-semibold text-gray-800">Operational address: </span>
                {BUSINESS.operationalAddress.display}
              </p>
              <Link
                to="/partnership-agreement"
                className="inline-flex items-center gap-2 mt-4 text-emerald-700 font-semibold text-sm hover:underline"
              >
                Partnership / Listing Agreement <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Stats */}
      <section className="bg-emerald-50 py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 gap-8 text-center max-w-md mx-auto">
            {stats.map((stat, i) => (
              <motion.div
                key={stat.label}
                initial={isPrerendered ? false : "hidden"}
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                custom={i}
              >
                <div className="text-3xl font-bold text-emerald-700 mb-1">{stat.value}</div>
                <div className="text-sm text-gray-500 font-medium">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Partner with us */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <motion.div
          initial={isPrerendered ? false : "hidden"}
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUp}
          className="rounded-2xl border border-gray-200 bg-white p-6 sm:p-8 shadow-sm"
        >
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Teach or partner with us</h2>
          <p className="text-gray-600 text-sm leading-relaxed mb-4">
            Individual yoga instructors can create a verified profile, publish online or prenatal offerings, and
            reach students on {BUSINESS.tradeName}. Yoga schools and retreat providers in India can apply as
            partners — you share your website; our team lists programs after review.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/become-an-instructor"
              className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 text-white px-4 py-2.5 text-sm font-semibold hover:bg-emerald-700"
            >
              Become an instructor <ArrowRight size={16} />
            </Link>
            <Link
              to="/become-a-partner"
              className="inline-flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-900 px-4 py-2.5 text-sm font-semibold hover:bg-emerald-100"
            >
              Become a partner <ArrowRight size={16} />
            </Link>
            <Link
              to="/partnership-agreement"
              className="inline-flex items-center gap-2 text-emerald-700 font-semibold text-sm hover:underline px-2 py-2.5"
            >
              Partnership / Listing Agreement
            </Link>
          </div>
        </motion.div>
      </section>

      {/* Philosophy */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <motion.div
          initial={isPrerendered ? false : "hidden"}
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUp}
        >
          <div className="flex items-center gap-3 justify-center mb-4">
            <Heart className="text-emerald-600" size={22} />
            <span className="text-emerald-600 font-semibold text-sm uppercase tracking-widest">
              Our Philosophy
            </span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 text-center mb-8">
            Wellness Is Not Escaping Life
          </h2>
          <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-8 sm:p-10 border border-emerald-100">
            <p className="text-lg text-gray-700 leading-relaxed mb-5 text-center italic">
              "We believe wellness is not about escaping life — it is about learning how to live it more fully."
            </p>
            <p className="text-gray-600 leading-relaxed text-center">
              Through daily yoga practice, meditation, breathwork, mindfulness, nourishing food, and meaningful
              connections, we help our guests create space for transformation that lasts long after the retreat ends.
            </p>
            <p className="text-gray-600 leading-relaxed text-center mt-4">
              Our programs welcome everyone — from complete beginners taking their first yoga class to experienced
              practitioners seeking a deeper connection with their practice.
            </p>
          </div>
        </motion.div>
      </section>

      {/* What Makes Us Different */}
      <section className="bg-gray-50 py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={isPrerendered ? false : "hidden"}
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="text-center mb-14"
          >
            <span className="text-emerald-600 font-semibold text-sm uppercase tracking-widest">
              Why Choose Us
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mt-2">
              What Makes Our Retreats Different
            </h2>
          </motion.div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {differentiators.map((item, i) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={item.title}
                  initial={isPrerendered ? false : "hidden"}
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  custom={i}
                  className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow border border-gray-100 flex flex-col gap-4"
                >
                  <div className="w-11 h-11 rounded-xl bg-emerald-100 flex items-center justify-center">
                    <Icon className="text-emerald-600" size={22} />
                  </div>
                  <h3 className="font-bold text-gray-900 text-base">{item.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{item.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
        <motion.div
          initial={isPrerendered ? false : "hidden"}
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUp}
        >
          <div className="flex items-center gap-3 justify-center mb-4">
            <Star className="text-emerald-600" size={22} />
            <span className="text-emerald-600 font-semibold text-sm uppercase tracking-widest">
              Our Mission
            </span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
            Making Wellness Accessible
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed max-w-2xl mx-auto">
            To make authentic yoga, meditation, and holistic wellness accessible to people from around the
            world while creating experiences that inspire{" "}
            <strong className="text-gray-900">healthier, happier, and more conscious living</strong>.
          </p>
        </motion.div>
      </section>

      {/* CTA */}
      <section className="bg-gradient-to-br from-emerald-700 to-teal-800 py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={isPrerendered ? false : "hidden"}
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
          >
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Begin Your Journey
            </h2>
            <p className="text-emerald-100 text-lg mb-8 leading-relaxed">
              Whether you are looking for a yoga retreat in India, a meditation retreat, or a yoga teacher
              training program — slow down, breathe deeply, and take the first step.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-xl bg-white text-emerald-700 font-semibold hover:bg-emerald-50 transition-colors shadow-lg"
              >
                Browse Retreats
                <ArrowRight size={18} />
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-xl bg-emerald-600/50 text-white font-semibold border border-emerald-400/50 hover:bg-emerald-600 transition-colors"
              >
                Contact Us
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

export default About;
